import type { ComponentType } from 'react';

import { Crown, SearchCheck } from 'lucide-react';
import { Link as RouterLink } from 'react-router';

import { useUserDetails } from '@/features/_shared';
import { toInlineStyle } from '@/features/_shared/lib/legacyStyle';
import { cn } from '@/lib/utils';
import { useAppDispatch } from '@/store/hooks';
import { closeDrawer } from '@/store/slices/drawer.slice';

export type ButtonType = 'expired' | 'manage' | 'upgrade';

const BUTTON_CONFIG: Record<
  ButtonType,
  { icon: ComponentType<{ className?: string }>; label: string }
> = {
  expired: { icon: Crown, label: 'Upgrade plan' },
  manage: { icon: SearchCheck, label: 'Manage subscription' },
  upgrade: { icon: Crown, label: 'Upgrade plan' }
};

const baseStyle = {
  minWidth: 250,
  outline: 'none',
  fontSize: 14
};

const sizeClassNames = {
  compact: 'h-6 px-2 text-xs',
  small: 'h-7 px-2.5 text-sm',
  medium: 'h-8 px-2.5 text-sm',
  large: 'h-9 px-3 text-base'
} as const;

const UpgradeToPremiumButton = ({
  sx,
  type,
  size,
  color
}: {
  sx?: unknown;
  type: ButtonType;
  size?: keyof typeof sizeClassNames;
  color?: 'primary' | 'secondary';
}) => {
  const { isUserExpired } = useUserDetails();
  const dispatch = useAppDispatch();

  const effectiveType = isUserExpired ? 'expired' : type;
  const { icon: Icon, label } = BUTTON_CONFIG[effectiveType];

  return (
    <RouterLink
      className={cn(
        'inline-flex min-w-[250px] items-center justify-center gap-1.5 rounded-lg bg-primary font-medium text-primary-foreground transition-colors hover:bg-primary/80 focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
        sizeClassNames[size ?? 'medium'],
        color === 'secondary' && 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
      )}
      style={{ ...baseStyle, ...toInlineStyle(sx) }}
      to='/manage-subscription'
      onClick={() => {
        dispatch(closeDrawer());
      }}
    >
      <Icon
        className='size-4'
        data-testid={effectiveType === 'manage' ? 'ManageSubscriptionIcon' : undefined}
      />
      {label}
    </RouterLink>
  );
};

export default UpgradeToPremiumButton;
